/**
 * Client-side limits for bulk receipt upload. These guard against unrealistic
 * batches up front — before any bytes are sent — so the user gets immediate,
 * per-file feedback instead of a server rejection after a slow upload.
 *
 * All four caps live here as named constants (no inline magic numbers at the
 * call sites) so the UI copy, the validator, and the fan-out all agree.
 */

/**
 * Cap on how many receipt uploads are in flight at once. Upload is a pure
 * transfer that hits no shared rate-limited resource, so this is only
 * connection politeness — not a server-side throttle.
 */
export const RECEIPT_UPLOAD_CONCURRENCY = 4

/** Most files a single bulk selection may stage for upload. */
export const RECEIPT_UPLOAD_MAX_FILES = 20

/** Per-file size cap, expressed in MB for user-facing copy. */
export const RECEIPT_UPLOAD_MAX_FILE_SIZE_MB = 10

/** Per-file size cap in bytes, derived from the MB value above. */
export const RECEIPT_UPLOAD_MAX_FILE_SIZE_BYTES = RECEIPT_UPLOAD_MAX_FILE_SIZE_MB * 1024 * 1024

/** MIME types the receipt uploader accepts (images + PDF). */
export const RECEIPT_UPLOAD_ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
] as const

/** Why a file was turned away before upload — the row renders a localized message per reason. */
export type RejectionReason = 'unsupported-type' | 'file-too-large' | 'too-many-files'

export interface FileRejection {
  file: File
  reason: RejectionReason
}

export interface FileValidationResult {
  accepted: File[]
  rejected: FileRejection[]
}

/**
 * Split an incoming selection into files that may upload and files turned away,
 * each with a reason. Checks run type → size → count so the most specific
 * reason wins. `existingCount` is how many valid files are already staged, so
 * the count cap applies to the running total, not just this batch.
 *
 * Rejection is per-file: one bad file never blocks the valid ones beside it,
 * and nothing is dropped silently — every excluded file comes back in
 * `rejected` with a reason the caller can surface.
 */
export const validateReceiptFiles = (
  incoming: readonly File[],
  existingCount = 0,
): FileValidationResult => {
  const accepted: File[] = []
  const rejected: FileRejection[] = []
  let count = existingCount

  for (const file of incoming) {
    if (!(RECEIPT_UPLOAD_ACCEPTED_TYPES as readonly string[]).includes(file.type)) {
      rejected.push({ file, reason: 'unsupported-type' })
      continue
    }
    if (file.size > RECEIPT_UPLOAD_MAX_FILE_SIZE_BYTES) {
      rejected.push({ file, reason: 'file-too-large' })
      continue
    }
    if (count >= RECEIPT_UPLOAD_MAX_FILES) {
      rejected.push({ file, reason: 'too-many-files' })
      continue
    }
    accepted.push(file)
    count++
  }

  return { accepted, rejected }
}
